/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Nguyen Dat Huu Trong (ndhtrong1@rsupartners.com)
 *
 * @description
 *
 * @created at Thu Dec 26 2024
 **/

import { useCallback, useLayoutEffect, useMemo } from "react";
import { useAtom } from "jotai";
import { EventHandler } from "@rfice/eventhandler";
import { EventID, SidebarID } from "@/frameworks/core/commons";
import { useSidebarActions, useSidebarState } from "@/frameworks/core/hooks";
import { SelectedMeeting } from "@/frameworks/core/components";

interface UseMeetingItemProps {
	meeting?: SwaggerMeetingAPIModels.Meeting;
	date?: string;
}

export default function useMeetingItem({ meeting, date }: UseMeetingItemProps) {
	const [selectedMeeting, setSelectedMeeting] = useAtom(SelectedMeeting);
	const sidebarActions = useSidebarActions();
	const sidebarState = useSidebarState();

	const isNoTimeline = useMemo(() => !(meeting && date), [meeting, date]);

	const selectKey = useMemo(() => {
		if (meeting && date) {
			return `${meeting.conferenceId || meeting.scheduleId}-${date}`;
		}
		return "";
	}, [meeting, date]);

	const isSelected = useMemo(() => {
		if (selectKey) {
			return selectKey === selectedMeeting;
		}
		return false;
	}, [selectKey, selectedMeeting]);

	const showDetailByMeetingstatus = useCallback((meeting: SwaggerMeetingAPIModels.Meeting) => {
		switch (meeting.status) {
			case "CLOSE":
				sidebarActions.show(SidebarID.PreviousMeeting);
				EventHandler.emit(EventID.OPEN_PAST_MEETING, meeting);
				break;
			case "OPEN":
				if (meeting.scheduleId) {
					sidebarActions.show(SidebarID.WaitingMeeting);
					EventHandler.emit(EventID.OPEN_MEETING_DETAIL, {
						scheduleId: meeting.scheduleId,
						conferenceId: meeting.conferenceId,
						startTime: meeting.scheduleStartTime || null
					});
				} else {
					sidebarActions.show(SidebarID.DetailMeeting);
					EventHandler.emit(EventID.OPEN_MEETING_DETAIL, {
						conferenceId: meeting.conferenceId
					});
				}
				break;
			case "WAIT":
				sidebarActions.show(SidebarID.WaitingMeeting);
				EventHandler.emit(EventID.OPEN_MEETING_DETAIL, {
					scheduleId: meeting.scheduleId,
					startTime: meeting.scheduleStartTime || null
				});
				break;
			default:
				break;
		}
	}, []);

	const openMeetingDetail = useCallback(
		(meeting: SwaggerMeetingAPIModels.Meeting) => {
			!isNoTimeline && setSelectedMeeting(selectKey);
			sidebarActions.hide(SidebarID.ConfirmMeetingReservation);
			sidebarActions.hide(SidebarID.CreateConference);
			sidebarActions.hide(SidebarID.MeetingReservation);
			sidebarActions.hide(SidebarID.WaitingMeeting);
			sidebarActions.hide(SidebarID.DetailMeeting);
			sidebarActions.hide(SidebarID.ReservationMeetingEdit);
			sidebarActions.hide(SidebarID.PreviousMeeting);
			sidebarActions.hide(SidebarID.PrivateMeetingDetail);
			showDetailByMeetingstatus(meeting);
		},
		[isNoTimeline]
	);

	const openPrivateMeetingDetail = useCallback(
		(meeting: SwaggerMeetingAPIModels.Meeting) => {
			!isNoTimeline && setSelectedMeeting(selectKey);
			sidebarActions.hide(SidebarID.ConfirmMeetingReservation);
			sidebarActions.hide(SidebarID.CreateConference);
			sidebarActions.hide(SidebarID.MeetingReservation);
			sidebarActions.hide(SidebarID.WaitingMeeting);
			sidebarActions.hide(SidebarID.DetailMeeting);
			sidebarActions.hide(SidebarID.ReservationMeetingEdit);
			sidebarActions.hide(SidebarID.PreviousMeeting);
			sidebarActions.hide(SidebarID.PrivateMeetingDetail);
			if (meeting.me?.owner || meeting.me?.member || meeting.me?.invited) {
				showDetailByMeetingstatus(meeting);
			} else {
				// not member
				sidebarActions.show(SidebarID.PrivateMeetingDetail);
				EventHandler.emit(EventID.OPEN_MEETING_DETAIL, {
					scheduleId: meeting.scheduleId || "",
					conferenceId: meeting.conferenceId || ""
				});
			}
		},
		[isNoTimeline]
	);

	const openPendingMeetingInfo = useCallback(
		(meeting: SwaggerMeetingAPIModels.Meeting) => {
			!isNoTimeline && setSelectedMeeting(selectKey);
			sidebarActions.hide(SidebarID.ConfirmMeetingReservation);
			sidebarActions.hide(SidebarID.CreateConference);
			sidebarActions.hide(SidebarID.MeetingReservation);
			sidebarActions.hide(SidebarID.PreviousMeeting);
			sidebarActions.hide(SidebarID.DetailMeeting);
			sidebarActions.hide(SidebarID.ReservationMeetingEdit);
			sidebarActions.hide(SidebarID.PrivateMeetingDetail);
			showDetailByMeetingstatus(meeting);
		},
		[isNoTimeline]
	);

	useLayoutEffect(() => {
		if (meeting && date) {
			if (
				sidebarState &&
				sidebarState.action === "hide" &&
				(sidebarState.id === SidebarID.PreviousMeeting || sidebarState.id === SidebarID.WaitingMeeting)
			) {
				setSelectedMeeting("");
			}
		}
	}, [sidebarState, meeting, date]);

	return {
		isSelected,
		openMeetingDetail,
		openPrivateMeetingDetail,
		openPendingMeetingInfo
	};
}
